import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable} from "rxjs";
import {ParticipantViewModel} from "../models/participant-view-model";
import {LoggedInUser} from "../models/user";
import * as jwt_decode from 'jwt-decode';
import {AccountTypeEnum} from "../models/account-type-enum";
import {AccessPolicyEnum, DefaultAccessPolicies, PermissionEnum} from "./guards/permissions";
import { filter, take } from 'rxjs/operators';
import { Mutex } from 'async-mutex';


@Injectable({
  providedIn: 'root'
})
export class UserDataStoreService {

  public mutex = new Mutex();

  private userAccountTypeSubject: BehaviorSubject<AccountTypeEnum>;

  private userPermissionsSubject: BehaviorSubject<string[]>;

  private userParticipantsSubject: BehaviorSubject<ParticipantViewModel[]>;

  private selectedParticipantSubject: BehaviorSubject<ParticipantViewModel>;


  public get accountType(): AccountTypeEnum {
    return this.userAccountTypeSubject.value;
  }

  public get currentUserPermissions(): string[] {
    return this.userPermissionsSubject.value;
  }

  public get selectedParticipantObservable(): Observable<ParticipantViewModel> {
    return this.selectedParticipantSubject.asObservable();
  }

  public get selectedParticipant(): ParticipantViewModel {
    return this.selectedParticipantSubject.value;
  }

  public get userParticipants(): ParticipantViewModel[] {
    return this.userParticipantsSubject.value;
  }

  private currentUserSubject: BehaviorSubject<LoggedInUser>;
  public currentUser: Observable<LoggedInUser>;

  public get currentUserValue(): LoggedInUser {
    return this.currentUserSubject.value;
  }

  constructor() {
    this.userPermissionsSubject = new BehaviorSubject<string[]>(JSON.parse(localStorage.getItem('userPermissions')));
    this.userParticipantsSubject = new BehaviorSubject<ParticipantViewModel[]>(JSON.parse(localStorage.getItem('userParticipants')));
    this.selectedParticipantSubject = new BehaviorSubject<ParticipantViewModel>(JSON.parse(localStorage.getItem('selectedParticipant')));
    this.currentUserSubject = new BehaviorSubject<LoggedInUser>(JSON.parse(localStorage.getItem('currentUser')));
    this.userAccountTypeSubject = new BehaviorSubject<AccountTypeEnum>(AccountTypeEnum[localStorage.getItem('accountType')]);
    this.currentUser = this.currentUserSubject.asObservable();
  }


  public UpdateUser(user: LoggedInUser): void {
    localStorage.setItem('currentUser', JSON.stringify(user));
    const decodedJWT = jwt_decode(user.token);
    const permissions: string[] = [];
    if (!!decodedJWT.AccessPermission) {
      if (Array.isArray(decodedJWT.AccessPermission)) {
        permissions.push(...decodedJWT.AccessPermission);
      } else {
        permissions.push(decodedJWT.AccessPermission);
      }
    }
    localStorage.setItem('userPermissions', JSON.stringify(permissions));
    this.userPermissionsSubject.next(permissions);


    localStorage.setItem('accountType', decodedJWT.AccountType);
    this.userAccountTypeSubject.next(AccountTypeEnum[localStorage.getItem('accountType')]);

    const userParticipants: ParticipantViewModel[] = [];
    if (!!user.userParticipants) {
      userParticipants.push(...user.userParticipants);
    }
    localStorage.setItem('userParticipants', JSON.stringify(userParticipants));
    this.userParticipantsSubject.next(userParticipants);

    if (!!decodedJWT.SelectedParticipant) {
      const selectedParticipant = user.userParticipants.find(x => x.id == decodedJWT.SelectedParticipant);
      this.selectedParticipantSubject.next(selectedParticipant);
      localStorage.setItem('selectedParticipant', JSON.stringify(selectedParticipant));
    } else {
      this.selectedParticipantSubject.next(null);
      localStorage.removeItem('selectedParticipant');
    }
    this.currentUserSubject.next(user);
  }

  public RefreshJWT(user: LoggedInUser): void {
    const updatedUser = JSON.parse(localStorage.getItem('currentUser'));
    updatedUser.token = user.token.replace('Bearer ', '');
    localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    this.currentUserSubject.next(updatedUser);
  }

  public Clear(): void {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('userParticipants');
    localStorage.removeItem('selectedParticipant');
    localStorage.removeItem('userPermissions');
    this.userPermissionsSubject.next(null);
    this.userParticipantsSubject.next(null);
    this.selectedParticipantSubject.next(null);
    this.currentUserSubject.next(null);
  }

  public hasRequiredPermissions(permissions: PermissionEnum[], policies?: AccessPolicyEnum[]): boolean {
    const checker = (arr, target) => target.every(v => arr?.includes(v));

    //Allow system admins all access
    if (this.accountType === AccountTypeEnum.SystemAdministrator) {
      return true;
    }

    let requiredPermissions = [];
    if (permissions) {
      requiredPermissions = [...permissions];
    }

    if (policies) {
      for (const policy of policies) {
        requiredPermissions = [...requiredPermissions, ...DefaultAccessPolicies[policy]];
      }
    }

    if (requiredPermissions && !checker(this.currentUserPermissions, requiredPermissions)) {
      return false;
    }
    // authorised so return true
    return true;
  }

}
